
import { supabase } from '@/integrations/supabase/client';

// Cookie consent functions
export interface CookieConsent {
  id?: string;
  user_id?: string;
  ip_address?: string;
  accepted: boolean;
  created_at?: string;
  updated_at?: string;
}

// Store a user's cookie consent
export const storeCookieConsent = async (consent: CookieConsent): Promise<boolean> => {
  try {
    // Make sure we have at least one identifier (user_id or ip_address)
    if (!consent.user_id && !consent.ip_address) {
      console.error('No user_id or ip_address provided for cookie consent');
      return false;
    }

    // Check if consent already exists for this user or IP
    let existingConsent: CookieConsent | null = null;
    
    if (consent.user_id) {
      const { data } = await supabase
        .from('cookie_consents')
        .select('*')
        .eq('user_id', consent.user_id)
        .maybeSingle();
      
      existingConsent = data;
    } else if (consent.ip_address) {
      const { data } = await supabase
        .from('cookie_consents')
        .select('*')
        .eq('ip_address', consent.ip_address)
        .is('user_id', null)
        .maybeSingle();
      
      existingConsent = data;
    }

    if (existingConsent) {
      // Update existing consent
      const { error } = await supabase
        .from('cookie_consents')
        .update({ 
          accepted: consent.accepted, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', existingConsent.id);
      
      if (error) throw error;
    } else {
      // Create new consent
      const { error } = await supabase
        .from('cookie_consents')
        .insert(consent);
      
      if (error) throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error storing cookie consent:', error);
    return false;
  }
};

// Update a user's cookie consent
export const updateCookieConsent = async (id: string, accepted: boolean): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('cookie_consents')
      .update({ accepted, updated_at: new Date().toISOString() })
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating cookie consent:', error);
    return false;
  }
};

// Get a user's cookie consent
export const getUserCookieConsent = async (userId: string): Promise<CookieConsent | null> => {
  try {
    const { data, error } = await supabase
      .from('cookie_consents')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting user cookie consent:', error);
    return null;
  }
};

// Get a cookie consent by IP address (for anonymous users)
export const getAnonymousCookieConsent = async (ipAddress: string): Promise<CookieConsent | null> => {
  try {
    const { data, error } = await supabase
      .from('cookie_consents')
      .select('*')
      .eq('ip_address', ipAddress)
      .is('user_id', null)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting anonymous cookie consent:', error);
    return null;
  }
};

// Get all cookie consents (for admin)
export const getAllCookieConsents = async (): Promise<CookieConsent[]> => {
  try {
    const { data, error } = await supabase
      .from('cookie_consents')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting all cookie consents:', error);
    return [];
  }
};
